import React from 'react';
import { motion } from 'framer-motion';
import { PersonaCopy } from '../data/personaCopy';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';

interface ProgressIndicatorProps {
  currentStep: string;
  progressPercentage: number;
  copy: PersonaCopy;
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
  currentStep, 
  progressPercentage, 
  copy 
}) => {
  const steps = [
    { id: 'email', label: 'Email Signup', description: 'Join the waitlist' },
    { id: 'questions', label: 'Personalization', description: 'Help us customize your experience' },
    { id: 'complete', label: 'All Set!', description: 'You\'re on the list' }
  ];

  const getStepStatus = (stepId: string) => {
    if (stepId === currentStep) return 'current';
    if (steps.findIndex(s => s.id === stepId) < steps.findIndex(s => s.id === currentStep)) {
      return 'completed';
    }
    return 'upcoming';
  };

  const getStepIcon = (stepId: string) => {
    const status = getStepStatus(stepId);
    
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'current':
        return <Circle className="w-5 h-5 text-primary-600 fill-current" />;
      default:
        return <Circle className="w-5 h-5 text-white/40" />;
    }
  };

  const getStepClass = (stepId: string) => {
    const status = getStepStatus(stepId);
    
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'current':
        return 'text-primary-600 font-semibold';
      default:
        return 'text-white/60';
    }
  };

  return (
    <section className="py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Progress Header */}
        <div className="text-center mb-8">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-2xl font-bold text-white mb-2"
          >
            Your Journey to Quellect
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white/80"
          >
            {copy.microcopy.progressIndicator
              .replace('{current}', Math.ceil(progressPercentage / 33.33).toString())
              .replace('{total}', '3')}
          </motion.p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="relative">
            {/* Background Bar */}
            <div className="w-full h-2 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
              <motion.div
                className="h-full bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              />
            </div>
            
            {/* Progress Percentage */}
            <div className="absolute -top-8 right-0">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="bg-primary-600 text-white text-xs font-medium px-2 py-1 rounded-full"
              >
                {progressPercentage}%
              </motion.div>
            </div>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((step, index) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className={`relative text-center ${
                getStepStatus(step.id) === 'current' ? 'z-10' : ''
              }`}
            >
              {/* Step Icon */}
              <div className="flex justify-center mb-3">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  getStepStatus(step.id) === 'completed' 
                    ? 'bg-green-100' 
                    : getStepStatus(step.id) === 'current'
                    ? 'bg-primary-100'
                    : 'bg-white/20 backdrop-blur-sm'
                }`}>
                  {getStepIcon(step.id)}
                </div>
              </div>

              {/* Step Label */}
              <h3 className={`font-semibold mb-1 ${getStepClass(step.id)}`}>
                {step.label}
              </h3>
              
              {/* Step Description */}
              <p className={`text-sm ${
                getStepStatus(step.id) === 'completed' 
                  ? 'text-green-600' 
                  : getStepStatus(step.id) === 'current'
                  ? 'text-primary-600'
                  : 'text-white/60'
              }`}>
                {step.description}
              </p>

              {/* Connection Line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-6 left-full w-full h-0.5 bg-white/20 transform translate-x-1/2">
                  <motion.div
                    className="h-full bg-gradient-to-r from-primary-500 to-secondary-500"
                    initial={{ width: 0 }}
                    animate={{ 
                      width: getStepStatus(step.id) === 'completed' ? '100%' : '0%' 
                    }}
                    transition={{ duration: 0.8, delay: 0.5 + index * 0.2 }}
                  />
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Current Step Highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm text-white px-4 py-2 rounded-full border border-white/20">
            <ArrowRight className="w-4 h-4" />
            <span className="text-sm font-medium">
              {currentStep === 'email' && 'Start by entering your email'}
              {currentStep === 'questions' && 'Help us personalize your experience'}
              {currentStep === 'complete' && 'You\'re all set!'}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProgressIndicator;
